import { useEffect, useState, useRef } from "react";
import { Loader2, Save, PlugZap, Server, AlertTriangle, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { fmtDateTime } from "@/lib/format";

function ConnectionCard({ c, state, onState, onAskCutover }) {
  const [form, setForm] = useState({ base_url: c.base_url, db: c.db, username: c.username, api_key: "" });
  const [busy, setBusy] = useState(null);
  const blockedDemo = c.key === "demo" && state.cutover_done;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const save = () => run("save", async () => {
    const r = await api.put(`/odoo/connections/${c.key}`, form);
    onState(r.data.data); setForm((f) => ({ ...f, api_key: "" })); toast.success(`${c.label} disimpan`);
  });
  const test = () => run("test", async () => {
    const r = await api.post(`/odoo/connections/${c.key}/test`);
    onState(r.data.data);
    r.data.data.result.ok ? toast.success(r.data.data.result.message) : toast.error(r.data.data.result.message);
  });
  const toggle = () => {
    if (!c.active && c.key === "production" && !state.cutover_done) { onAskCutover(); return; }
    run("toggle", async () => {
      const r = await api.post(`/odoo/connections/${c.key}/activate`, { enabled: !c.active });
      onState(r.data.data); toast.success(c.active ? `${c.label} dinonaktifkan` : `${c.label} diaktifkan`);
    });
  };

  return (
    <article className={`conn-card ${c.key} ${c.active ? "active" : ""}`} data-testid={`conn-card-${c.key}`}>
      <header>
        <div className="conn-title"><Server size={18} /><div><strong>{c.label}</strong>
          <small>{c.active ? "Aktif — dipakai untuk sinkronisasi" : blockedDemo ? "Terkunci setelah pindah ke Production" : "Nonaktif"}</small></div></div>
        <button className={`rj-switch ${c.active ? "on" : ""}`} onClick={toggle} disabled={!!busy || blockedDemo || (!c.complete && !c.active)}
          title={!c.complete ? "Lengkapi & simpan data koneksi dulu" : ""} data-testid={`conn-toggle-${c.key}`} aria-label={`Aktifkan ${c.label}`}><i /></button>
      </header>
      <div className="conn-form">
        <label className="rj-field">URL Odoo<input className="rj-input" value={form.base_url} onChange={set("base_url")} placeholder="https://perusahaan.odoo.com" data-testid={`conn-url-${c.key}`} /></label>
        <label className="rj-field">Database<input className="rj-input" value={form.db} onChange={set("db")} placeholder="nama-database" data-testid={`conn-db-${c.key}`} /></label>
        <label className="rj-field">Username / Email login<input className="rj-input" value={form.username} onChange={set("username")} placeholder="admin@perusahaan.com" data-testid={`conn-user-${c.key}`} /></label>
        <label className="rj-field">API Key
          <input className="rj-input" type="password" autoComplete="new-password" value={form.api_key} onChange={set("api_key")}
            placeholder={c.api_key_set ? `Tersimpan (••••${c.api_key_hint}) — isi untuk mengganti` : "Tempel API Key dari Odoo"} data-testid={`conn-key-${c.key}`} />
        </label>
      </div>
      {c.last_test && (
        <div className={`conn-test ${c.last_test.ok ? "ok" : "fail"}`} data-testid={`conn-test-result-${c.key}`}>
          {c.last_test.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {c.last_test.message} · {fmtDateTime(c.last_test.at)}
        </div>
      )}
      <footer>
        <button className="rj-btn rj-btn-green rj-btn-sm" onClick={save} disabled={!!busy || blockedDemo} data-testid={`conn-save-${c.key}`}>
          {busy === "save" ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Simpan
        </button>
        <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={test} disabled={!!busy || !c.complete} data-testid={`conn-test-${c.key}`}>
          {busy === "test" ? <Loader2 size={14} className="spin" /> : <PlugZap size={14} />} Uji Koneksi
        </button>
        {c.updated_at && <small>Diubah {fmtDateTime(c.updated_at)}{c.updated_by && c.updated_by !== "env" ? ` · ${c.updated_by}` : ""}</small>}
      </footer>
    </article>
  );
}

function CutoverDialog({ state, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const poll = useRef(null);
  useEffect(() => () => clearInterval(poll.current), []);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/odoo/connections/production/activate", { enabled: true, confirm: text });
      setJob({ status: "running", step: "Memulai…", done: 0, total: 10 });
      poll.current = setInterval(async () => {
        try {
          const r = await api.get("/odoo/cutover/status");
          setJob(r.data.data);
          if (r.data.data.status !== "running") clearInterval(poll.current);
        } catch { /* session ends during cutover; keep polling */ }
      }, 2000);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const relogin = () => { localStorage.removeItem("rj_token"); window.location.reload(); };

  return (
    <Modal open onClose={job?.status === "running" ? undefined : onClose} className="cutover-dialog" title="Pindah ke Odoo Production" testId="cutover-dialog"
      footer={!job ? <>
        <button className="rj-btn rj-btn-ghost" onClick={onClose} data-testid="cutover-cancel-btn">Batal</button>
        <button className="rj-btn rj-btn-primary" onClick={start} disabled={busy || text.trim().toUpperCase() !== state.confirm_text} data-testid="cutover-confirm-btn">
          {busy ? <Loader2 size={16} className="spin" /> : "Hapus data demo & pindah"}
        </button>
      </> : job.status !== "running" && <button className="rj-btn rj-btn-green" onClick={relogin} data-testid="cutover-relogin-btn">Login ulang</button>}>
      {!job ? (
        <>
          <div className="void-warning"><ShieldAlert size={16} /><span><b>Tindakan ini tidak bisa dibatalkan.</b> Seluruh data demo di semua menu akan dihapus bersih:
            Sales Order, pengiriman, invoice & pembayaran, customer, produk, pricelist, kunjungan, wilayah distributor, notifikasi, log, dan semua pengguna.</span></div>
          <ul className="cutover-list">
            <li>Hanya 1 pengguna yang disisakan: <b>{state.prod_admin_email}</b> (Administrator).</li>
            <li>Setelah dihapus, data disinkronkan ulang dari Odoo Production.</li>
            <li>Semua sesi login berakhir — login kembali dengan {state.prod_admin_email}.</li>
            <li>Koneksi Demo dikunci agar data tidak tercampur.</li>
          </ul>
          <label className="rj-field">Ketik <b>{state.confirm_text}</b> untuk konfirmasi
            <input className="rj-input" value={text} onChange={(e) => setText(e.target.value)} data-testid="cutover-confirm-input" />
          </label>
        </>
      ) : (
        <div className="cutover-progress" data-testid="cutover-progress">
          {job.status === "running" && <Loader2 className="spin" />}
          {job.status === "done" && <CheckCircle2 className="ok" />}
          {job.status === "error" && <AlertTriangle className="fail" />}
          <strong>{job.status === "done" ? "Pemindahan ke Production selesai" : job.status === "error" ? "Pemindahan gagal" : job.step}</strong>
          <div className="rj-progress-track"><div className="rj-progress-bar" style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} /></div>
          <small>{job.status === "error" ? job.error : job.status === "done" ? `Silakan login dengan ${job.prod_admin_email || state.prod_admin_email}.` : `${job.done || 0}/${job.total || "…"} langkah`}</small>
        </div>
      )}
    </Modal>
  );
}

export function ConnectionPanel() {
  const [state, setState] = useState(null);
  const [cutover, setCutover] = useState(false);
  useEffect(() => { api.get("/odoo/connections").then((r) => setState(r.data.data)).catch((e) => toast.error(apiError(e))); }, []);

  if (!state) return <div className="panel"><div className="rj-loading"><Loader2 className="spin" /> Memuat koneksi…</div></div>;
  return (
    <div className="panel conn-panel" data-testid="odoo-connection-panel">
      <div className="panel-head"><div><h3>Koneksi Odoo</h3>
        <p>Isi data koneksi Odoo Production & Demo, lalu aktifkan salah satu. {state.cutover_done ? "Production sudah aktif sejak pemindahan data." : "Mengaktifkan Production akan menghapus seluruh data demo."}</p></div></div>
      <div className="conn-grid">
        {state.connections.map((c) => (
          <ConnectionCard key={`${c.key}-${c.updated_at}`} c={c} state={state} onState={setState} onAskCutover={() => setCutover(true)} />
        ))}
      </div>
      {cutover && <CutoverDialog state={state} onClose={() => setCutover(false)} />}
    </div>
  );
}
